import JSZip from 'jszip';

/** Returns the first captured number from the text, or null if there isn't one. */
function firstNumber(text: string | undefined, pattern: RegExp): number | null {
  const captured = text?.match(pattern)?.[1];
  if (captured === undefined) return null;
  const value = parseInt(captured, 10);
  return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * Reads the page/slide count stored inside an Office file.
 * Returns null when the file doesn't carry a usable count.
 * DOCX/ODT: the count saved by the last app that wrote the file (can be stale).
 * PPTX: number of slides (exact).
 */
export async function getOfficePageCount(file: File): Promise<number | null> {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  const zip = await JSZip.loadAsync(file);

  if (ext === 'docx') {
    const xml = await zip.file('docProps/app.xml')?.async('string');
    return firstNumber(xml, /<Pages>\s*(\d+)\s*<\/Pages>/);
  }

  if (ext === 'pptx') {
    const slides = Object.keys(zip.files).filter((name) => /^ppt\/slides\/slide\d+\.xml$/.test(name));
    return slides.length > 0 ? slides.length : null;
  }

  if (ext === 'odt') {
    const xml = await zip.file('meta.xml')?.async('string');
    return firstNumber(xml, /meta:page-count="(\d+)"/);
  }

  return null; // doc, rtf, txt: can't be read in a browser
}